/**
 * The fatigue scale. Six levels, 1 (Completely Drained) to 6 (Fully
 * Energised) — a battery's charge, so a HIGHER number means LESS fatigue.
 *
 * The names are six of the seven on the printed chart; Slowing Down is the
 * one left out. The number is shown alongside the name and colour — patients
 * pick "3 Need Rest", not just a feeling — and is what gets stored. `SCALE` is
 * in score order, so `scalePoint` looks a score up by its offset from
 * MIN_SCORE; displays that want 6 at the top reverse it. The wording is part of the product and is shown
 * verbatim — do not paraphrase `name`, and do not add
 * or remove levels once real entries exist, because changing the scale
 * invalidates every entry already recorded against it.
 */

export type Score = 1 | 2 | 3 | 4 | 5 | 6;

export interface ScalePoint {
  readonly score: Score;
  /** How the level feels. Always shown with the number. */
  readonly name: string;
  /** Ramp colour, used identically in both applications. Never the only channel. */
  readonly colour: string;
  /**
   * The same step, darkened for text and thin strokes.
   *
   * The ramp above is a fill colour: on a light background #F0BC44 is 1.8:1,
   * which is fine as an area and unreadable as text. Every one of these
   * clears 4.5:1 on white, so anything drawn as text or a 1px stroke uses
   * `ink` and anything drawn as an area uses `colour`.
   */
  readonly ink: string;
}

export const SCALE: readonly ScalePoint[] = [
  { score: 1, name: 'Completely Drained', colour: '#8E1F14', ink: '#8E1F14' },
  { score: 2, name: 'Extreme Exhaustion', colour: '#D04A27', ink: '#CB4826' },
  { score: 3, name: 'Need Rest', colour: '#E17932', ink: '#B15F27' },
  { score: 4, name: 'Noticeable Fatigue', colour: '#F0BC44', ink: '#8F7028' },
  { score: 5, name: 'Mild Tiredness', colour: '#95B35B', ink: '#667B3E' },
  { score: 6, name: 'Fully Energised', colour: '#679C5A', ink: '#547F49' },
] as const;

export const MIN_SCORE = 1 satisfies Score;
export const MAX_SCORE = 6 satisfies Score;

/** Near the middle. The patient app no longer pre-selects it; kept because the contract carries it. */
export const DEFAULT_SCORE = 3 satisfies Score;

export function isScore(value: unknown): value is Score {
  return (
    typeof value === 'number' && Number.isInteger(value) && value >= MIN_SCORE && value <= MAX_SCORE
  );
}

export function scalePoint(score: Score): ScalePoint {
  const point = SCALE[score - MIN_SCORE];
  if (point === undefined) {
    throw new RangeError(`No scale point for score ${String(score)}`);
  }
  return point;
}

export const scoreName = (score: Score): string => scalePoint(score).name;
export const scoreColour = (score: Score): string => scalePoint(score).colour;
export const scoreInk = (score: Score): string => scalePoint(score).ink;

/** How a level is written wherever one is shown: "3 · Need Rest". */
export const scoreLabel = (score: Score): string => `${score} · ${scoreName(score)}`;

/**
 * The level nearest a fractional score, for summarising a mean in words.
 * Clamped, so a stray value outside the scale still names a real level.
 */
export function nearestPoint(value: number): ScalePoint {
  const rounded = Math.min(MAX_SCORE, Math.max(MIN_SCORE, Math.round(value)));
  return scalePoint(rounded as Score);
}
